import { useState, useEffect, useCallback } from 'react';
import Echo from 'laravel-echo';
import SocketConnection from '@/service/socketService';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';

function listen(socket: Echo, channel: string, event: string, callBack: (payload: any) => void) {
  console.log('connect to channel: ' + channel);
  socket.private(channel).listen(event, (payload: any) => {
    callBack(payload);
  });

  return function cleanUp() {
    console.log('disconnect to channel: ' + channel);
    socket.leaveChannel(`private-${channel}`);
  };
}

type Options = {
  type: 'JOIN_CHAT' | 'CONNECT_NOTICE' | 'CONNECT_INBOX' | 'JOIN_CHATLIST';
  callBack: (payload: any) => void;
  roomId?: number;
};

export const useSocket = ({ type, callBack, roomId }: Options) => {
  const auth = useAppSelector(getAuthSelector);
  const { accessToken, userData, isLogin } = auth;
  const [socket, setSocket] = useState<Echo | null>(null);

  const handleSocketClose = useCallback(() => {
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    const reconnectInterval = 3000; // 3秒

    if (!isLogin || !accessToken) return;

    if (reconnectAttempts < maxReconnectAttempts) {
      reconnectAttempts++;
      setTimeout(() => {
        const socketManager = SocketConnection.getInstance(accessToken);
        setSocket(socketManager.getEchoInstance() as Echo);
      }, reconnectInterval);
    } else {
      console.log('無法建立WebSocket連線');
    }
  }, [isLogin, accessToken]);

  useEffect(() => {
    // 若使用者未登入，不建立連線
    if (!isLogin || !accessToken) {
      if (socket) {
        console.log('disconnect...');
        socket.disconnect();
        setSocket(null);
      }
      return;
    }

    // 若已有連線，不重複建立
    if (socket) {
      return;
    }

    // 建立新連線
    console.log('connecting...');
    const socketManager = SocketConnection.getInstance(accessToken);
    const newSocket = socketManager.getEchoInstance() as Echo;
    setSocket(newSocket);

    const socketConnect = newSocket.connector.socket;
    if (socketConnect) {
      socketConnect.onclose = handleSocketClose;
    }
  }, [isLogin, accessToken, socket, handleSocketClose]);

  useEffect(() => {
    if (!socket || !isLogin) return;

    let cleanup: (() => void) | undefined;

    switch (type) {
      case 'CONNECT_NOTICE': {
        cleanup = listen(socket, `App.User.${userData.id}`, '.NotificationEvent', callBack);
        break;
      }
      case 'CONNECT_INBOX': {
        cleanup = listen(socket, `App.User.${userData.id}`, '.NotificationInboxChanged', callBack);
        break;
      }
      case 'JOIN_CHAT': {
        if (!roomId) return;
        cleanup = listen(socket, `Chatroom.${roomId}`, '.MessageEvent', callBack);
        break;
      }
      case 'JOIN_CHATLIST': {
        cleanup = listen(socket, `User.${userData.id}.Chatrooms`, '.ChatroomEvent', callBack);
        break;
      }
    }

    return cleanup;
  }, [socket, isLogin, type, roomId, userData.id, callBack]);
};
